const SSO_API_URL = process.env.REACT_APP_SSO_API_URL ?? '';

const AUTH_HEADERS_STORAGE_KEY = 'celepus_auth_headers';

interface AuthHeaders {
  'access-token': string;
  client: string;
  uid: string;
  expiry?: string;
  'token-type'?: string;
}

const getStoredAuthHeaders = (): AuthHeaders | null => {
  const raw = localStorage.getItem(AUTH_HEADERS_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const setStoredAuthHeaders = (headers: AuthHeaders) => {
  localStorage.setItem(AUTH_HEADERS_STORAGE_KEY, JSON.stringify(headers));
};

const clearStoredAuthHeaders = () => {
  localStorage.removeItem(AUTH_HEADERS_STORAGE_KEY);
};

const isLoggedIn = (): boolean => {
  return getStoredAuthHeaders() !== null;
};

class ApiError extends Error {
  status: number;
  body: any;

  constructor(status: number, body: any) {
    super(ApiError.messageFrom(body, status));
    this.status = status;
    this.body = body;
  }

  private static messageFrom(body: any, status: number): string {
    if (body?.errors) {
      const errors = body.errors;
      if (Array.isArray(errors)) {
        return errors.join(', ');
      }
      if (typeof errors === 'object') {
        return Object.values(errors).flat().join(', ');
      }
    }
    if (body?.validationErrors) {
      return Object.values(body.validationErrors).flat().join(', ');
    }
    if (body?.message) {
      return body.message;
    }
    return `Request failed with status ${status}`;
  }
}

// DeviseTokenAuth issues a new access-token on (almost) every authenticated
// request, and the old one stops working — so the headers from each
// response have to be saved back to storage, or the user gets logged out
// after their second request.
const updateStoredAuthHeadersFromResponse = (response: Response) => {
  const accessToken = response.headers.get('access-token');
  if (!accessToken) {
    return;
  }

  setStoredAuthHeaders({
    'access-token': accessToken,
    client: response.headers.get('client') ?? '',
    uid: response.headers.get('uid') ?? '',
    expiry: response.headers.get('expiry') ?? undefined,
    'token-type': response.headers.get('token-type') ?? undefined,
  });
};

const apiFetch = async (path: string, options: RequestInit = {}): Promise<any> => {
  const storedHeaders = getStoredAuthHeaders();

  const response = await fetch(`${SSO_API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(storedHeaders ?? {}),
      ...(options.headers as Record<string, string> | undefined),
    },
  });

  updateStoredAuthHeadersFromResponse(response);

  const text = await response.text();
  const body = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new ApiError(response.status, body);
  }

  return body;
};

export {
  SSO_API_URL,
  apiFetch,
  ApiError,
  getStoredAuthHeaders,
  setStoredAuthHeaders,
  clearStoredAuthHeaders,
  isLoggedIn,
  updateStoredAuthHeadersFromResponse,
};
